import { TypedFormFieldComponents } from '@fl/cmsch-fe-library';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Opt } from 'ts-opt';
import { Accordion } from 'api/gen/Accordion';
import { AssociationType } from 'api/gen/AssociationType';
import { CriteriaDefinition } from 'api/gen/CriteriaDefinition';
import { useForm } from 'utils/forms';
import { MatingBullsFilterFormValues, matingCowsFilterFormName, MatingCowsFilterFormValues, matingCowsInitialFormValues } from '../../types';
import { BottomButtons } from '../animals-filter/bottom-buttons';
import { AnimalsFormContainer } from '../animals-form-containter';
import { CriteriasInCollapse } from '../criteria-collapse';
import { ExtraFirstAccordionNodes } from './extra-first-accordion-nodes';
import { createWarn, validate } from './validate';
import { matingAction, matingSelector } from '../../model';
const FEMALE = 'FEMALE';
const antFormProps = {
  layout: 'horizontal',
  labelAlign: 'left',
  labelCol: {
    xs: {
      span: 24
    },
    sm: {
      span: 6
    },
    xl: {
      span: 5
    }
  },
  wrapperCol: {
    xs: {
      span: 24
    },
    sm: {
      span: 18
    },
    xl: {
      span: 19
    }
  },
  labelWrap: true
} as const;
interface Props {
  /** Association type used by the filter form. */
  associationType: AssociationType;
  /** Accordion definitions shown in the filter form. */
  criteriaAccordions: Opt<Array<Accordion>>;
  /** Criteria definitions shown in the filter form. */
  criteriaDefinitions: Opt<Array<CriteriaDefinition>>;
  /** Whether no saved criteria lists exist. */
  criteriaListsEmpty: boolean;
  /** Whether no saved animal lists exist. */
  animalListsEmpty: boolean;
  /** Whether the animals page is currently empty. */
  isAnimalsPageEmpty: boolean;
  /** Handles form submission. */
  onSubmit(): void;
  /** Opens or closes the saved animal lists modal. */
  toggleAnimalListsVisibility(): void;
  /** Opens or closes the add-animal modal. */
  toggleAddAnimalsVisibility(): void;
  /** Opens or closes the add-from-DB modal. */
  toggleAddAnimalsFromDBVisibility(): void;
  /** Opens or closes the overview modal for saved lists. */
  toggleListsOverviewVisibility(): void;
  /** Opens or closes the saved criteria lists modal. */
  toggleCriteriaListsVisibility(): void;
  /** Opens or closes the criteria-list-details modal. */
  toggleCriteriaListDetailsVisibility(): void;
}
const MatingCowsFilterFormBase: FC<Props> = props => {
  const {
    associationType,
    criteriaAccordions,
    criteriaDefinitions,
    criteriaListsEmpty,
    animalListsEmpty,
    isAnimalsPageEmpty,
    onSubmit,
    toggleAnimalListsVisibility,
    toggleAddAnimalsVisibility,
    toggleAddAnimalsFromDBVisibility,
    toggleListsOverviewVisibility,
    toggleCriteriaListsVisibility,
    toggleCriteriaListDetailsVisibility
  } = props;
  const dispatch = useDispatch();
  const resetCowsAccordionValues = useCallback((accordionId: number): void => {
    dispatch(matingAction.resetAccordionValues(FEMALE, accordionId));
  }, [dispatch]);
  const resetCowsCriteriaValue = useCallback((criteriaCode: Opt<string>): void => {
    dispatch(matingAction.resetSingleCriteriaValue(FEMALE, criteriaCode.orElse('')));
  }, [dispatch]);
  const getFarms = useCallback(() => void dispatch(matingAction.getFarms()), [dispatch]);
  const minimumFemaleAgeInMonths = useSelector(matingSelector.minimumFemaleAgeInMonths).orNull();
  const warningValidator = useMemo(() => createWarn(minimumFemaleAgeInMonths), [minimumFemaleAgeInMonths]);
  const {
    Form,
    Fields,
    valid,
    submitting
  } = useForm({
    form: matingCowsFilterFormName,
    initialValues: matingCowsInitialFormValues,
    onSubmit,
    validate,
    warn: warningValidator
  }, antFormProps);
  const breeders = useSelector(matingSelector.breeders);
  const farms = useSelector(matingSelector.farms);
  const extraFirstAccordionNodes = useMemo(() => <ExtraFirstAccordionNodes breeders={breeders} farms={farms} getFarms={getFarms} Fields={Fields} />, [breeders, farms, getFarms, Fields]);
  const criteriaAccordionNodes = useMemo(() => <CriteriasInCollapse criteriaAccordions={criteriaAccordions.orElse([])} criteriaDefinitions={criteriaDefinitions.orElse([])} extraFirstAccordionNodes={extraFirstAccordionNodes} resetCowsAccordionValues={resetCowsAccordionValues} resetCriteriaValue={resetCowsCriteriaValue}
  // TODO: improve type after updating typescript to 4.7+
  Fields={Fields as TypedFormFieldComponents<MatingCowsFilterFormValues | MatingBullsFilterFormValues>} />, [Fields, criteriaAccordions, criteriaDefinitions, extraFirstAccordionNodes, resetCowsAccordionValues, resetCowsCriteriaValue]);
  const bottomButtons = useMemo(() => <BottomButtons sex={FEMALE} associationType={associationType} valid={valid} submitting={submitting} criteriaListsEmpty={criteriaListsEmpty} animalListsEmpty={animalListsEmpty} isAnimalsPageEmpty={isAnimalsPageEmpty} toggleAnimalListsVisibility={toggleAnimalListsVisibility} toggleAddAnimalsVisibility={toggleAddAnimalsVisibility} toggleAddAnimalsFromDBVisibility={toggleAddAnimalsFromDBVisibility} toggleListsOverviewVisibility={toggleListsOverviewVisibility} toggleCriteriaListsVisibility={toggleCriteriaListsVisibility} toggleCriteriaListDetailsVisibility={toggleCriteriaListDetailsVisibility} />, [associationType, valid, submitting, criteriaListsEmpty, animalListsEmpty, isAnimalsPageEmpty, toggleAnimalListsVisibility, toggleAddAnimalsVisibility, toggleAddAnimalsFromDBVisibility, toggleListsOverviewVisibility, toggleCriteriaListsVisibility, toggleCriteriaListDetailsVisibility]);
  return useMemo(() => <AnimalsFormContainer>
            <Form>
                {criteriaAccordionNodes}
                {bottomButtons}
            </Form>
        </AnimalsFormContainer>, [Form, bottomButtons, criteriaAccordionNodes]);
};

/**
 * Memoized cow filter form used by the mating workflow.
 *
 * @param props Filter state and modal toggles.
 * @returns The cow filter form.
 */
export const MatingCowsFilterForm = memo(MatingCowsFilterFormBase);